import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import { Box, Stack, Tooltip, Typography } from "@mui/material";
import MuiMenu, { IMuiMenuItemProps } from "../../framework/muiMenu";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DeleteIcon from "@mui/icons-material/Delete";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../framework/customStore";
import {
    clearConfirmation,
    setConfirmation,
    showErrorMessage,
    showSuccessMessage,
} from "../../../models/store/storeActions";
import { ConfirmationDialogType } from "../../../models/store/storeTypes";
import * as dayBookingService from "../../../services/dayBookingService";
import { ApiError } from "../../../services/baseService";
import { fetchDayBookings } from "../../../store/workShiftPlanningSlice";
import {
    DayBookingItemBeta,
    getDurationStrBasedOnUnit,
} from "../../../models/dayBooking/dayBookingItemBeta";
import { DayBookingTypeItemBeta } from "../../../models/dayBookingType/dayBookingTypeItemBeta";
import {
    DayBookingUsageType,
    StrMeasureUnit,
} from "../../../models/common/enums";
import { EmployeeListItemDto } from "../../../models/employee/employee";
import { getDatesForRange } from "../../timelinePlanner/timelineUtils";
import {
    DATE_COLUMN_WIDTH,
    TimelineGridContext,
} from "../../timelinePlanner/timelinePlannerGrid";

const DAY_UNITS = new Set<string>([
    StrMeasureUnit.Day,
    StrMeasureUnit.DayFiAlt,
    StrMeasureUnit.DayEn,
].map((unit) => unit.toString().toLowerCase()));

const GRID_CELL_HORIZONTAL_PADDING = 8;
const PILL_SECTION_GAP = 4;
const DEFAULT_BAR_HEIGHT = 36;
const DEFAULT_PILL_HEIGHT = 28;
const HEIGHT_EPSILON = 0.5;

interface EmployeeDayBookingsProps {
    bookings: DayBookingItemBeta[];
    dayBookingTypes: Record<string, DayBookingTypeItemBeta>;
    onColumnHeightsChange?: (heights: Record<string, number>) => void;
    employees: EmployeeListItemDto[];
}

type BookingWithType = {
    booking: DayBookingItemBeta;
    type?: DayBookingTypeItemBeta;
    startDate?: string;
    endDate?: string;
    employeeIds: string[];
};

type BookingGroup = Record<string, DayBookingItemBeta[]>;

const normalizeUnit = (unit?: string) => (unit ?? "").toString().toLowerCase();

const isDayUnit = (unit?: string) => DAY_UNITS.has(normalizeUnit(unit));

const resolveEndDay = (booking: DayBookingItemBeta) => {
    if (typeof booking.getEndDay === "function") {
        const endDay = booking.getEndDay();
        if (endDay) {
            return endDay;
        }
    }
    return booking.endDay || booking.day;
};

const isDayBasedBooking = (
    booking: DayBookingItemBeta,
    dayBookingTypes: Record<string, DayBookingTypeItemBeta>
) => {
    const type = dayBookingTypes?.[booking.dayBookingTypeId];
    const unit = booking.salaryRowTypeMeasureUnit || type?.salaryRowTypeMeasureUnit;
    return (
        booking.dayUsageType === DayBookingUsageType.FullDay ||
        isDayUnit(unit)
    );
};

const compareBookings = (a: DayBookingItemBeta, b: DayBookingItemBeta) => {
    const startA = a.day ?? "";
    const startB = b.day ?? "";
    if (startA !== startB) return startA.localeCompare(startB);

    const endA = resolveEndDay(a) ?? "";
    const endB = resolveEndDay(b) ?? "";
    if (endA !== endB) return endA.localeCompare(endB);

    return (a.id ?? "").localeCompare(b.id ?? "");
};

const addBookingToDay = (
    groups: BookingGroup,
    day: string | undefined,
    booking: DayBookingItemBeta
) => {
    if (!day) return;
    if (!groups[day]) {
        groups[day] = [];
    }
    groups[day].push(booking);
};

const formatAmount = (
    booking: DayBookingItemBeta,
    onlyOneDay = false
) => {
    if (!booking.salaryRowTypeMeasureUnit) {
        return "";
    }

    return getDurationStrBasedOnUnit(
        booking.amount,
        booking.dayUsageType,
        booking.dayHoursRatio,
        booking.salaryRowTypeMeasureUnit,
        onlyOneDay
    );
};

const formatDateRange = (booking: DayBookingItemBeta, dateOverride?: string) => {
    const start = booking.day;
    const end = resolveEndDay(booking);

    if (dateOverride) {
        return dateOverride;
    }

    if (!start) {
        return "";
    }

    if (!end || start === end) {
        return start;
    }

    return `${start} – ${end}`;
};

const createTooltip = (
    booking: DayBookingItemBeta,
    type: DayBookingTypeItemBeta | undefined,
    amountLabel: string,
    dateOverride?: string
) => {
    const lines: string[] = [];
    const displayName = type?.name || booking.dayBookingTypeId;
    const rangeLabel = formatDateRange(booking, dateOverride);

    if (displayName) lines.push(displayName);
    if (rangeLabel) lines.push(rangeLabel);
    if (amountLabel) lines.push(amountLabel);
    if (booking.comment) lines.push(booking.comment);

    return lines.length > 0 ? lines.join("\n") : "";
};
const getRelevantDates = (booking: DayBookingItemBeta) => {
    const dates = getDatesForRange(booking.day, resolveEndDay(booking));
    if (dates && dates.length > 0) {
        return dates;
    }
    return booking.day ? [booking.day] : [];
};

const isApproximatelyEqual = (a: number | undefined, b: number, epsilon = HEIGHT_EPSILON) => {
    return typeof a === "number" && Math.abs(a - b) < epsilon;
};

const DayBookingBar = ({
    booking,
    type,
    startDate,
    endDate,
    employeeIds,
}: BookingWithType) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const [showTooltip, setShowTooltip] = useState(true);

    const amountLabel = formatAmount(booking);
    const tooltip = createTooltip(booking, type, amountLabel);

    const handleDeleteShift = () => {
        if (!booking.id) {
            return;
        }

        dispatch(
            setConfirmation(
                ConfirmationDialogType.Warning,
                t("common.warning"),
                t("workShiftPlanning.confirmDeleteDaybooking"),
                () => {
                    dispatch(clearConfirmation());

                    dayBookingService
                        .deleteDayBooking(booking.id)
                        .then(() => {
                            dispatch(showSuccessMessage(t("common.deleteSuccess")));
                            if (startDate && endDate) {
                                void dispatch(
                                    fetchDayBookings({
                                        startDate,
                                        endDate,
                                        employeeIds,
                                    }) as any
                                );
                            }
                        })
                        .catch((err: ApiError) =>
                            dispatch(
                                showErrorMessage(
                                    err?.status === 404
                                        ? t("workTime.daybookingNotFound")
                                        : t("workShiftPlanning.deleteDaybookingFailed")
                                )
                            )
                        );
                },
                () => dispatch(clearConfirmation())
            )
        );
    };

    const menuItems: IMuiMenuItemProps[] = [];
    const isLocked = typeof booking.isLocked === "function" ? booking.isLocked() : false;
    if (booking.id && !isLocked) {
        menuItems.push({
            label: t("common.remove"),
            icon: <DeleteIcon fontSize="small" sx={{ color: "error.main" }} />,
            onClick: handleDeleteShift,
        });
    }

    const content = (
        <Box className="day-booking-bar">
            <Box
                className="day-booking-bar__content"
                sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr auto 1fr",
                    alignItems: "center",
                    width: "100%",
                    columnGap: 1,
                }}
            >
                {amountLabel ? (
                    <Typography
                        variant="caption"
                        className="day-booking-bar__amount"
                        noWrap
                        sx={{ justifySelf: "start", minWidth: 0 }}
                    >
                        {amountLabel}
                    </Typography>
                ) : (
                    <Box />
                )}
                <Typography
                    variant="caption"
                    className="day-booking-bar__label"
                    noWrap
                    sx={{ textAlign: "center", minWidth: 0 }}
                >
                    {type?.name || booking.dayBookingTypeId}
                </Typography>
                {menuItems.length > 0 ? (
                    <Box sx={{ justifySelf: "end", minWidth: 0 }}>
                        <MuiMenu
                            label={<MoreVertIcon fontSize="small" />}
                            size="small"
                            items={menuItems}
                            buttonSx={{ p: 0.5 }}
                            noBox
                            showTooltips={setShowTooltip}
                        />
                    </Box>
                ) : (
                    <Box />
                )}
            </Box>
        </Box>
    );

    if (!tooltip) {
        return content;
    }

    return (
        <Tooltip title={showTooltip ? <span style={{ whiteSpace: "pre-line" }}>{tooltip}</span> : ""}>
            {content}
        </Tooltip>
    );
};
interface DayBookingPillProps {
    booking: DayBookingItemBeta;
    type?: DayBookingTypeItemBeta;
    date: string;
}
const DayBookingPill = ({ booking, type, date }: DayBookingPillProps) => {
    const amountLabel = formatAmount(booking);
    const tooltip = createTooltip(booking, type, amountLabel, date);
    const content = (
        <Box className="day-booking-pill">
            <Stack
                direction="row"
                spacing={0.5}
                alignItems="center"
                className="day-booking-pill__content"
            >
                <Typography
                    variant="caption"
                    className="day-booking-pill__label"
                    noWrap
                >
                    {type?.name || booking.dayBookingTypeId}
                </Typography>
                {amountLabel ? (
                    <Typography
                        variant="caption"
                        className="day-booking-pill__amount"
                        noWrap
                    >
                        {amountLabel}
                    </Typography>
                ) : null}
            </Stack>
        </Box>
    );

    return tooltip ? (
        <Tooltip title={<span style={{ whiteSpace: "pre-line" }}>{tooltip}</span>}>
            {content}
        </Tooltip>
    ) : (
        content
    );
};

interface AbsoluteBookingCellProps {
    left: number;
    width: number;
    top: number;
    className: string;
    children: React.ReactNode;
    onResize?: (height: number) => void;
}

const AbsoluteBookingCell = ({
    left,
    width,
    top,
    className,
    children,
    onResize,
}: AbsoluteBookingCellProps) => {
    const ref = React.useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (!ref.current || !onResize) {
            return;
        }

        const element = ref.current;
        const notify = () => {
            onResize(element.getBoundingClientRect().height);
        };

        notify();

        if (typeof ResizeObserver === "undefined") {
            return undefined;
        }

        const observer = new ResizeObserver(() => notify());
        observer.observe(element);

        return () => observer.disconnect();
    }, [onResize]);

    return (
        <div
            ref={ref}
            className={`timeline-day-booking-cell ${className}`}
            style={{
                position: "absolute",
                top,
                left,
                width,
                boxSizing: "border-box",
            }}
        >
            {children}
        </div>
    );
};

export const EmployeeDayBookings = React.memo(
    ({ bookings, dayBookingTypes, onColumnHeightsChange, employees }: EmployeeDayBookingsProps) => {
        const context = useContext(TimelineGridContext);
        const { startDate, endDate } = useAppSelector(
            (state) => state.workShiftPlanning.filters
        );

        const [barRowHeights, setBarRowHeights] = useState<Record<number, number>>({});
        const [pillHeights, setPillHeights] = useState<Record<string, number>>({});

        const employeeIds = useMemo(
            () => employees.map(({ id }) => id).filter((id): id is string => Boolean(id)),
            [employees]
        );

        const getBookingKey = useCallback((booking: DayBookingItemBeta, rowIndex: number) => {
            return booking.id ?? `${booking.day ?? ""}-${booking.dayBookingTypeId}-${rowIndex}`;
        }, []);

        const dayBasedBookings = useMemo(() => {
            return bookings
                .filter((booking) => isDayBasedBooking(booking, dayBookingTypes))
                .map<BookingWithType>((booking) => ({
                    booking,
                    type: dayBookingTypes?.[booking.dayBookingTypeId],
                    startDate,
                    endDate,
                    employeeIds,
                }))
                .sort((a, b) => compareBookings(a.booking, b.booking));
        }, [bookings, dayBookingTypes, startDate, endDate, employeeIds]);

        const dayBasedBookingsWithRow = useMemo(() => {
            const occupied: Record<string, Set<number>> = {};

            return dayBasedBookings.map((item) => {
                const { booking } = item;
                const relevantDates = getRelevantDates(booking);

                let row = 1;
                const isRowOccupied = (candidate: number) =>
                    relevantDates.some((date) => occupied[date]?.has(candidate));

                while (isRowOccupied(row)) {
                    row += 1;
                }

                relevantDates.forEach((date) => {
                    const set = occupied[date] ?? (occupied[date] = new Set<number>());
                    set.add(row);
                });

                return {
                    ...item,
                    rowIndex: row,
                };
            });
        }, [dayBasedBookings]);

        const perDaySegments = useMemo(() => {
            const groups: BookingGroup = {};

            bookings.forEach((booking) => {
                if (isDayBasedBooking(booking, dayBookingTypes)) {
                    return;
                }

                const endDay = resolveEndDay(booking);
                const dates = getDatesForRange(booking.day, endDay);

                if (!dates || dates.length === 0) {
                    addBookingToDay(groups, booking.day, booking);
                    return;
                }

                dates.forEach((date) => addBookingToDay(groups, date, booking));
            });

            Object.values(groups).forEach((list) => list.sort(compareBookings));
            return groups;
        }, [bookings, dayBookingTypes]);

        const daySegmentEntries = useMemo(() => {
            return Object.entries(perDaySegments).sort(([a], [b]) => a.localeCompare(b));
        }, [perDaySegments]);

        const { barTopByBooking, barBottomByDate } = useMemo(() => {
            const perDateHeight: Record<string, number> = {};
            const topOffsets: Record<string, number> = {};
            const rows = Array.from(new Set(dayBasedBookingsWithRow.map((item) => item.rowIndex))).sort((a, b) => a - b);

            rows.forEach((row) => {
                const rowHeight = barRowHeights[row] ?? DEFAULT_BAR_HEIGHT;
                dayBasedBookingsWithRow
                    .filter((item) => item.rowIndex === row)
                    .forEach(({ booking, rowIndex }) => {
                        const relevantDates = getRelevantDates(booking);

                        if (relevantDates.length === 0) {
                            return;
                        }

                        const top = relevantDates.reduce(
                            (max, date) => Math.max(max, perDateHeight[date] ?? 0),
                            0
                        );

                        topOffsets[getBookingKey(booking, rowIndex)] = top;

                        relevantDates.forEach((date) => {
                            perDateHeight[date] = Math.max(
                                perDateHeight[date] ?? 0,
                                top + rowHeight
                            );
                        });
                    });
            });

            return { barTopByBooking: topOffsets, barBottomByDate: perDateHeight };
        }, [dayBasedBookingsWithRow, barRowHeights, getBookingKey]);

        const pillTopByDate = useMemo(() => {
            const result: Record<string, number> = {};

            Object.keys(perDaySegments).forEach((date) => {
                const base = barBottomByDate[date] ?? 0;
                result[date] = base > 0 ? base + PILL_SECTION_GAP : 0;
            });

            return result;
        }, [perDaySegments, barBottomByDate]);

        const columnHeights = useMemo(() => {
            const heights: Record<string, number> = { ...barBottomByDate };

            Object.entries(perDaySegments).forEach(([date, items]) => {
                if (items.length === 0) {
                    return;
                }

                const top = pillTopByDate[date] ?? 0;
                const measured = pillHeights[date] ?? DEFAULT_PILL_HEIGHT;
                const bottom = top + measured;
                heights[date] = Math.max(heights[date] ?? 0, bottom);
            });

            return heights;
        }, [barBottomByDate, perDaySegments, pillHeights, pillTopByDate]);

        useEffect(() => {
            onColumnHeightsChange?.(columnHeights);
        }, [columnHeights, onColumnHeightsChange]);

        const computeHorizontalMetrics = useCallback(
            (rangeStart: string | undefined, rangeEnd: string | undefined) => {
                if (!context || !rangeStart) {
                    return null;
                }

                const startColumn = context.getColumnForDate(rangeStart);
                if (!startColumn) {
                    return null;
                }

                const endColumn = context.getColumnForDate(rangeEnd ?? rangeStart) || startColumn;
                const span = Math.max(1, endColumn - startColumn + 1);
                const left = (startColumn - 1) * DATE_COLUMN_WIDTH + GRID_CELL_HORIZONTAL_PADDING;
                const width = Math.max(
                    0,
                    span * DATE_COLUMN_WIDTH - GRID_CELL_HORIZONTAL_PADDING * 2
                );

                return { left, width };
            },
            [context]
        );

        const handleBarHeightChange = useCallback((rowIndex: number, height: number) => {
            setBarRowHeights((prev) => {
                if (isApproximatelyEqual(prev[rowIndex], height)) {
                    return prev;
                }
                return { ...prev, [rowIndex]: height };
            });
        }, []);

        const handlePillHeightChange = useCallback((date: string, height: number) => {
            setPillHeights((prev) => {
                if (isApproximatelyEqual(prev[date], height)) {
                    return prev;
                }
                return { ...prev, [date]: height };
            });
        }, []);

        return (
            <>
                {dayBasedBookingsWithRow.map(({
                    booking,
                    type,
                    rowIndex,
                    startDate: bookingStart,
                    endDate: bookingEnd,
                    employeeIds: bookingEmployeeIds,
                }) => {
                    const metrics = computeHorizontalMetrics(
                        booking.day,
                        resolveEndDay(booking)
                    );

                    if (!metrics) {
                        return null;
                    }

                    const bookingKey = getBookingKey(booking, rowIndex);
                    const top = barTopByBooking[bookingKey] ?? 0;

                    return (
                        <AbsoluteBookingCell
                            key={bookingKey}
                            className="timeline-day-booking-cell--bar"
                            left={metrics.left}
                            width={metrics.width}
                            top={top}
                            onResize={(height) => handleBarHeightChange(rowIndex, height)}
                        >
                            <DayBookingBar
                                booking={booking}
                                type={type}
                                startDate={bookingStart}
                                endDate={bookingEnd}
                                employeeIds={bookingEmployeeIds}
                            />
                        </AbsoluteBookingCell>
                    );
                })}

                {daySegmentEntries.map(([date, items]) => {
                    if (!items || items.length === 0) {
                        return null;
                    }

                    const metrics = computeHorizontalMetrics(date, date);
                    if (!metrics) {
                        return null;
                    }

                    const top = pillTopByDate[date] ?? 0;

                    return (
                        <AbsoluteBookingCell
                            key={date}
                            className="timeline-day-booking-cell--pill"
                            left={metrics.left}
                            width={metrics.width}
                            top={top}
                            onResize={(height) => handlePillHeightChange(date, height)}
                        >
                            <Stack spacing={0.5}>
                                {items.map((booking) => (
                                    <DayBookingPill
                                        key={`${booking.id}-${date}`}
                                        booking={booking}
                                        type={dayBookingTypes?.[booking.dayBookingTypeId]}
                                        date={date}
                                    />
                                ))}
                            </Stack>
                        </AbsoluteBookingCell>
                    );
                })}
            </>
        );
    }
);

EmployeeDayBookings.displayName = "EmployeeDayBookings";
